import React, { Dispatch } from "react";

import { LazyComponentWithPreload, lazyWithPreload } from "@/utils/lazyPreload";
import { localStorageManager } from "@/utils/localStorageManager";
import { checkIsPaywallStep, LocalStorageManagerKeys } from "@/utils/localStorageManager";
import { FlowTypes } from "@/utils/providers/types";
import { step } from "@/utils/settings";

const Main = lazyWithPreload(() => import("@/components/Main"));
const NameScreen = lazyWithPreload(() => import("@/components/NameScreen/index"));
const EmailScreen = lazyWithPreload(() => import("@/components/EmailScreen/EmailScreen"));
const EmailSubscribeScreen = lazyWithPreload(() => import("@/components/EmailSubscribeScreen"));
const Loader = lazyWithPreload(() => import("@/components/Promo/LoaderPromo"));
const Deeplink = lazyWithPreload(() => import("@/components/Deeplink Screen/Deeplink"));
const BodyScan = lazyWithPreload(() => import("@/components/Upsells/BodyScan/BodyScan"));
const OneFoldBodyScan = lazyWithPreload(
    () => import("@/components/Upsells/OneFoldUpsells/OneFoldBodyScan")
);
const OneFoldNutritionGuide = lazyWithPreload(
    () => import("@/components/Upsells/OneFoldUpsells/OneFoldNutritionGuide")
);
const OneFoldSupplementsGuide = lazyWithPreload(
    () => import("@/components/Upsells/OneFoldUpsells/OneFoldSupplementsGuide")
);
const OneFoldNutritionSupplementsBundle = lazyWithPreload(
    () => import("@/components/Upsells/OneFoldUpsells/OneFoldNutritionSupplementsBundle")
);
const NewBodyScan = lazyWithPreload(() => import("@/components/Upsells/NewBodyScan/index"));
const NewBodyScanSimplified = lazyWithPreload(
    () => import("@/components/Upsells/NewBodyScanSimplified/index")
);
const NutritionGuide = lazyWithPreload(
    () => import("@/components/Upsells/NutritionGuide/NutritionGuide")
);
const ProgressLoader = lazyWithPreload(() => import("@/components/ProgressLoader/ProgressLoader"));
const CircularLoader = lazyWithPreload(() => import("@/components/Loader/CircularLoader"));
const DateChart = lazyWithPreload(() => import("@/components/Promo/DateChart"));
const ActionPlan = lazyWithPreload(() => import("@/components/ActionPlan"));
const AuthForm = lazyWithPreload(() => import("@/components/AuthForm"));
const ChartImage = lazyWithPreload(() => import("@/components/Prepaywall/ChartImage"));
const AbandonCartPaywall = lazyWithPreload(
    () => import("@/components/Paywall/AbandonCartPaywall/index")
);
const MainPaymentPaywall = lazyWithPreload(() => import("@/components/Paywall/MainPaymentPaywall"));
const AIWeightPredict = lazyWithPreload(
    () => import("@/components/AIWeightPredict/WeightPredictScreen")
);
const BodyScanPrepaywall = lazyWithPreload(() => import("@/components/BodyScanPrepaywall"));
const AlreadyWithUs = lazyWithPreload(() => import("@/components/AlreadyWithUs"));
const WhatsappConnectionIntroduction = lazyWithPreload(
    () => import("@/components/WhatsappConnection/Introduction")
);
const WhatsappConnectionConfirmPhone = lazyWithPreload(
    () => import("@/components/WhatsappConnection/ConfirmPhone")
);
const WhatsappConnectionInvitationSent = lazyWithPreload(
    () => import("@/components/WhatsappConnection/InvitationSent")
);
const SupplementsGuide = lazyWithPreload(() => import("@/components/Upsells/SupplementsGuide"));

const GiftOnEmailScreen = lazyWithPreload(() => import("@/components/GiftOnEmailScreen"));

const NutritionSupplementsBundle = lazyWithPreload(
    () => import("@/components/Upsells/NutritionSupplementsBundle")
);

const LesMillsUpgrade = lazyWithPreload(() => import("@/components/Upsells/LesMillsUpgrade/index"));

Main.preload();

export const enum COMPONENTS {
    QUESTIONS = "Questions",
    PROGRESS_LOADER = "ProgressLoader",
    FIRST_LOADER = "FirstLoader",
    LOADER = "Loader",
    EMAIL = "Email",
    EMAIL_SUBSCRIBE = "EmailSubscribe",
    CHART = "Chart",
    PAYWALL = "Paywall",
    DEEPLINK = "Deeplink",
    NUTRITION_GUIDE = "NutritionGuide",
    BODY_SCAN = "BodyScan",
    NEW_BODY_SCAN = "NewBodyScan",
    NEW_BODY_SCAN_SIMPLIFIED = "NewBodyScanSimplified",
    DATE_CHART = "DateChart",
    AI_PAYWALL = "AiPaywall",
    AUTH_FORM = "AuthForm",
    ACTION_PLAN = "ACTION_PLAN",
    AI_WEIGHT_PREDICT = "AiWeightPredict",
    BODY_SCAN_PREPAYWALL = "BodyScanPrepaywall",
    ALREADY_WITH_US = "AlreadyWithUs",
    NAME = "NameScreen",
    WHATSAPP_CONNECTION_MAIN = "WhatsappConnectionIntroduction",
    WHATSAPP_CONNECTION_PHONE = "WhatsappConnectionConfirmPhone",
    WHATSAPP_CONNECTION_DONE = "WhatsappConnectionInvitationSent",
    SUPPLEMENTS_GUIDE = "SupplementsGuide",
    GIFT_ON_EMAIL_SCREEN = "GiftOnEmailScreen",
    NUTRITION_SUPPLEMENTS_BUNDLE = "NutritionPlusSupplementsBundle",
    ONE_FOLD_BODY_SCAN = "OneFoldBodyScan",
    ONE_FOLD_NUTRITION_GUIDE = "OneFoldNutritionGuide",
    ONE_FOLD_SUPPLEMENTS_GUIDE = "OneFoldSupplementsGuide",
    ONE_FOLD_NUTRITION_SUPPLEMENTS_BUNDLE = "OneFoldNutritionSupplementsBundle",
    LES_MILLS_UPGRADE = "LesMillsUpgrade",
}

export interface CommonFlowComponentProps {
    setComponent: Dispatch<React.SetStateAction<COMPONENTS>>;
}
export const components: { [key in COMPONENTS]?: LazyComponentWithPreload } = {
    [COMPONENTS.FIRST_LOADER]: Loader,
    [COMPONENTS.QUESTIONS]: Main,
    [COMPONENTS.EMAIL]: EmailScreen,
    [COMPONENTS.EMAIL_SUBSCRIBE]: EmailSubscribeScreen,
    [COMPONENTS.DEEPLINK]: Deeplink,
    [COMPONENTS.BODY_SCAN]: BodyScan,
    [COMPONENTS.ONE_FOLD_BODY_SCAN]: OneFoldBodyScan,
    [COMPONENTS.NEW_BODY_SCAN]: NewBodyScan,
    [COMPONENTS.NEW_BODY_SCAN_SIMPLIFIED]: NewBodyScanSimplified,
    [COMPONENTS.NUTRITION_GUIDE]: NutritionGuide,
    [COMPONENTS.ONE_FOLD_NUTRITION_GUIDE]: OneFoldNutritionGuide,
    [COMPONENTS.ONE_FOLD_SUPPLEMENTS_GUIDE]: OneFoldSupplementsGuide,
    [COMPONENTS.ONE_FOLD_NUTRITION_SUPPLEMENTS_BUNDLE]: OneFoldNutritionSupplementsBundle,
    [COMPONENTS.PROGRESS_LOADER]: ProgressLoader,
    [COMPONENTS.LOADER]: CircularLoader,
    [COMPONENTS.DATE_CHART]: DateChart,
    [COMPONENTS.ACTION_PLAN]: ActionPlan,
    [COMPONENTS.AUTH_FORM]: AuthForm,
    [COMPONENTS.CHART]: ChartImage,
    [COMPONENTS.PAYWALL]: AbandonCartPaywall,
    [COMPONENTS.AI_PAYWALL]: MainPaymentPaywall,
    [COMPONENTS.AI_WEIGHT_PREDICT]: AIWeightPredict,
    [COMPONENTS.BODY_SCAN_PREPAYWALL]: BodyScanPrepaywall,
    [COMPONENTS.ALREADY_WITH_US]: AlreadyWithUs,
    [COMPONENTS.NAME]: NameScreen,
    [COMPONENTS.WHATSAPP_CONNECTION_MAIN]: WhatsappConnectionIntroduction,
    [COMPONENTS.WHATSAPP_CONNECTION_PHONE]: WhatsappConnectionConfirmPhone,
    [COMPONENTS.WHATSAPP_CONNECTION_DONE]: WhatsappConnectionInvitationSent,
    [COMPONENTS.SUPPLEMENTS_GUIDE]: SupplementsGuide,
    [COMPONENTS.GIFT_ON_EMAIL_SCREEN]: GiftOnEmailScreen,
    [COMPONENTS.NUTRITION_SUPPLEMENTS_BUNDLE]: NutritionSupplementsBundle,
    [COMPONENTS.LES_MILLS_UPGRADE]: LesMillsUpgrade,
};

export const getInitComponent = ({ flowType }: { flowType: FlowTypes | null }) => {
    if (flowType === FlowTypes.Email || flowType === FlowTypes.EmailBodyScan) {
        return COMPONENTS.PAYWALL;
    }
    if (localStorageManager.getStoredItem<boolean>(LocalStorageManagerKeys.IS_AUTH_PASSED)) {
        return COMPONENTS.DEEPLINK;
    }
    if (localStorageManager.getStoredItem<boolean>(LocalStorageManagerKeys.IS_AUTH_STEP)) {
        return COMPONENTS.AUTH_FORM;
    }
    if (flowType === FlowTypes.FunnelFox) {
        return COMPONENTS.AI_PAYWALL;
    }
    if (checkIsPaywallStep()) {
        return flowType === FlowTypes.Friends ? COMPONENTS.PAYWALL : COMPONENTS.AI_PAYWALL;
    }
    if (step) {
        return (step.charAt(0).toUpperCase() + step.slice(1)) as COMPONENTS;
    }
    if (flowType === FlowTypes.Both || flowType === FlowTypes.Friends) {
        return COMPONENTS.QUESTIONS;
    }

    return COMPONENTS.QUESTIONS;
};
